import { PropsWithChildren, RefObject, useLayoutEffect } from "react";
import { useOnClickOutside } from "usehooks-ts";

import { useDropdownMenuContext } from "../../DropdownMenuProvider";
import clsx from "clsx";

export type DropdownMenuContentDimensions = {
  height: number;
  width: number;
};

export type DropdownMenuContentProps = {
  className?: string;
  "data-testid"?: string;
  style?: React.CSSProperties;
  onOpenStateChanged?: (dimensions: DropdownMenuContentDimensions) => void;
};

export const DropdownMenuContent = ({
  children,
  className: additionalClasses,
  "data-testid": dataTestId,
  style: additionalStyles,
  onOpenStateChanged,
}: PropsWithChildren<DropdownMenuContentProps>) => {
  const { isOpen, menuPositionStyleProperties, menuWidthClassName, closeMenu, contentPortalContainerRef } =
    useDropdownMenuContext();

  useLayoutEffect(() => {
    if (isOpen) {
      onOpenStateChanged?.({
        height: contentPortalContainerRef.current?.getBoundingClientRect().height ?? 0,
        width: contentPortalContainerRef.current?.getBoundingClientRect().width ?? 0,
      });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- onOpenStateChanged omitted to match prior behavior

  const handleClickOutside = () => {
    if (isOpen === false) return;
    closeMenu();
  };
  useOnClickOutside(contentPortalContainerRef as RefObject<HTMLElement>, handleClickOutside);

  return (
    <>
      {isOpen && (
        <div
          ref={contentPortalContainerRef as RefObject<HTMLDivElement>}
          data-testid={dataTestId ?? "dropdown-menu-content"}
          style={{
            ...menuPositionStyleProperties,
            ...additionalStyles,
          }}
          className={clsx(
            "fixed",
            "box-border",
            "bg-white",
            "rounded-sm",
            "shadow-md",
            "z-50",
            menuWidthClassName,
            additionalClasses
          )}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      )}
    </>
  );
};
